import React, { useEffect, useState, useRef, useCallback } from 'react';
import Navbar from '../../navbar';
import Header from '../../header';
import { useDispatch, useSelector } from 'react-redux';
import classes from './style.module.css'
import { getFormMandatoryFields } from '../../../store/reducers/applicationSlice';
import { createNotification } from '../../../store/reducers/notificationSlice';
import FormHeader from '../forms/formHeader/formHeader';
import FormFooter from '../forms/formFooter/formFooter';
import AddAttachment from '../applications/attachments';
import FormLoader from '../forms/formLoader';
import { useNavigate, useParams } from "react-router-dom";
import { showError } from '../../../helpers/toast';
import { prepareHtmlForPdf } from '../../../utility/utility';
import { createDraftPdfThenStatus } from '../../../helpers/applicationSaveFlow';

const AppForm = () => {
  const [formIds, setFormIds] = useState([]);
  const [inputFields, setInputFields] = useState({});
  const [pageIds, setPageIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const accessToken = useSelector((state) => state.auth.user.access_token);
  const user = useSelector((state) => state.auth.user);
  const { applyingFormData } = useSelector((state) => state.counter)
  const [signatureIds, setSignatureIds] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const dispatch = useDispatch();

  // Reference to attachments component's saveChanges function
  const attachmentsRef = useRef(null);

  const navigate = useNavigate();
  const params = useParams();

  // Page visibility management - hide all pages and show only current page
  useEffect(() => {
    if (pageIds?.length > 0) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Hide all pages first
        pageIds.forEach(id => {
          const pageDiv = document.getElementById(id);
          if (pageDiv) {
            pageDiv.style.display = "none";
          }
        });

        // Show only the current page
        const currentPageDiv = document.getElementById(`page${currentPage}`);
        if (currentPageDiv) {
          currentPageDiv.style.display = "block";
        }
      });
    }
  }, [pageIds, currentPage]);

  // Initialize first page when form loads
  useEffect(() => {
    if (pageIds?.length > 0 && currentPage === 1) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Show first page when form loads
        const firstPageDiv = document.getElementById('page1');
        if (firstPageDiv) {
          firstPageDiv.style.display = "block";
        }
      }, 100);
    }
  }, [pageIds, currentPage]);

  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = useCallback((event) => {
    const { id, value } = event.target;
    setInputFields(prevInputFields => ({
      ...prevInputFields,
      [id]: value,
    }));

  }, []);

  const fetchApplicationMandatoryFields = async () => {
    const data = await getFormMandatoryFields(params.id, accessToken);
    if (data) {
      return data[0]?.mandatory_fields;
    }
  };

  const handleCheckBoxChange = useCallback((event) => {
    const { id, checked, value } = event.target;

    setInputFields(prevInputFields => {
      // If the checkbox is checked, add it to inputFields
      if (checked) {
        return {
          ...prevInputFields,
          [id]: value,
        };
      } else {
        // If the checkbox is unchecked, remove it from inputFields
        const { [id]: removedValue, ...remainingFields } = prevInputFields;
        return remainingFields;
      }
    })
  }, []);

  const attachEventHandlers = useCallback(() => {
    let tempSignatureIds = []
    formIds.forEach(id => {
      const inputField = document.getElementById(id);
      if (inputField) {
        if (inputField.type === 'image') {
          tempSignatureIds.push(id);

        } else if (inputField.type === 'checkbox') {
          inputField.addEventListener('change', handleCheckBoxChange)
        }
        else {
          inputField.addEventListener('change', handleInputChange);
        }
      }

    });
    setSignatureIds(tempSignatureIds);
  }, [formIds, handleInputChange, handleCheckBoxChange]);

  useEffect(() => {
    if (formIds?.length > 0) {
      attachEventHandlers();
    }
  }, [formIds?.length, attachEventHandlers]);

  const areAllMandatoryFieldsFilled = (inputFields, mandatoryFields) => {
    return mandatoryFields.every((fieldId) => {
      const value = inputFields[fieldId];
      return value !== undefined && value !== null && value !== '';
    });
  };

  /**
   * First-time submit: create (drafts) → PDF → status.
   */
  async function handleSubmitWithLoading(e) {
    setIsLoading(true);
    try {
      const mandatory_fields = await fetchApplicationMandatoryFields();
      let isValid = true;
      if (mandatory_fields) {
        isValid = areAllMandatoryFieldsFilled(inputFields, mandatory_fields);
      }
      if (!isValid) {
        showError("Please fill all mandatory fields.");
        return;
      }

      const htmlForm = document.getElementById("DocumentPDF")?.outerHTML;
      const finalState =
        user.user?.user_role === "employee"
          ? "application_submitted_by_company_user_to_company"
          : "in_process";

      const result = await createDraftPdfThenStatus(dispatch, {
        form: applyingFormData?.name,
        name: applyingFormData?.name,
        category: applyingFormData?.category,
        user_id: user.user.id,
        token: accessToken,
        files: uploadedFiles,
        json_data: inputFields,
        htmlForm: prepareHtmlForPdf(htmlForm, inputFields, pageIds),
        nextState: finalState,
        successMessage: "Application submitted successfully!",
      });

      if (!result.ok) return;

      const applicationId = result.applicationId;
      if (user.user?.user_role === "employee") {
        dispatch(createNotification({
          user_id: user.user.id,
          applicationId,
          applicationName: applyingFormData?.name,
          status: "submitted",
          accessToken: accessToken,
          sendToCompanyAdmins: true,
          companyName: user.user?.company_name,
          employeeName: user.user?.full_name
        }));
        navigate("/in-review");
      } else {
        dispatch(createNotification({
          user_id: user.user.id,
          applicationId,
          applicationName: applyingFormData?.name,
          status: "company_submitted",
          accessToken: accessToken,
          sendToCompanyAdmins: true,
          sendToGamingBoardAdmins: true,
          sendToGamingBoardReviewers: true,
          sendToGamingBoardViewers: true,
          companyName: user.user?.company_name,
          employeeName: user.user?.full_name
        }));
        navigate("/in-process-applications");
      }
    } catch (error) {
      console.error("Submit application error:", error);
      showError(error?.message || "Failed to submit application");
    } finally {
      setIsLoading(false);
    }
  };

  async function saveToDrafts(e) {
    setIsLoading(true);
    try {
      const htmlForm = document.getElementById("DocumentPDF")?.outerHTML;
      const finalState = typeof e === "string" && e ? e : "drafts";

      const result = await createDraftPdfThenStatus(dispatch, {
        form: applyingFormData?.name,
        name: applyingFormData?.name,
        category: applyingFormData?.category,
        user_id: user.user.id,
        token: accessToken,
        files: uploadedFiles,
        json_data: inputFields,
        htmlForm: prepareHtmlForPdf(htmlForm, inputFields, pageIds),
        nextState: finalState,
        successMessage: "Application saved to drafts successfully!",
      });

      if (!result.ok) return;

      dispatch(createNotification({
        user_id: user.user.id,
        applicationId: result.applicationId,
        applicationName: applyingFormData?.name,
        status: "drafts",
        accessToken: accessToken,
        sendToCompanyAdmins: true
      }));
      navigate(`/draft/${result.applicationId}`);
    } catch (error) {
      console.error("Save draft error:", error);
      showError(error?.message || "Failed to save draft");
    } finally {
      setIsLoading(false);
    }
  };

  // Function to call saveChanges from attachments component
  const handleSaveChanges = async () => {
    if (attachmentsRef.current && attachmentsRef.current.saveChanges) {
      await attachmentsRef.current.saveChanges();
    } else {
    }
  };
  const previousPage = () => {
    if (currentPage === pageIds?.length + 1) {
      setShowAttachments(false);
      showDiv(currentPage - 1);
      setCurrentPage(currentPage - 1)
    } else {
      if (currentPage - 1 >= 1) {
        hideDiv(currentPage);
        showDiv(currentPage - 1);
        setCurrentPage(currentPage - 1);
      }
    }
  }

  const nextPage = () => {
    if (currentPage + 1 <= pageIds?.length) {
      hideDiv(currentPage);
      showDiv(currentPage + 1);
      setCurrentPage(currentPage + 1);
    } else if (currentPage + 1 <= pageIds?.length + 1) {
      hideDiv(currentPage);
      setCurrentPage(currentPage + 1);
      setShowAttachments(true);
    }
  }
  const hideDiv = (pageNo) => {
    const pageDiv = document.getElementById("page" + pageNo);
    pageDiv.style.display = "none";
  }

  const showDiv = (pageNo) => {
    const pageDiv = document.getElementById("page" + pageNo);
    pageDiv.style.display = "";
  }

  const handlePageChange = (number) => {
    if (number > 0 && number <= pageIds?.length + 1) {
      if (number === pageIds?.length + 1) {
        // Hide current page and show attachments
        hideDiv(currentPage);
        setCurrentPage(number);
        setShowAttachments(true);
      } else {
        // Hide attachments and show the requested page
        setShowAttachments(false);

        // Hide current page and show new page
        if (currentPage !== pageIds?.length + 1) {
          hideDiv(currentPage);
        }

        // Small delay to ensure smooth transition
        setTimeout(() => {
          showDiv(number);
          setCurrentPage(number);
        }, 50);
      }
    }
  }

  return (
    <div>
      <Navbar />
      <Header />
      <div className='wrapper'>
        <div className='boxAppBoard boxWhite'>
          <div className="stickyFormHeader">
            <FormHeader
              applicationName={applyingFormData.name}
              nextPage={nextPage}
              previousPage={previousPage}
              currentPage={currentPage}
              totalPages={pageIds?.length + 1}
              handleSubmitWithLoading={handleSubmitWithLoading}
              saveToDrafts={saveToDrafts}
              handlePageChange={handlePageChange}
              saveChanges={handleSaveChanges}
            />
          </div>

          <div className={classes['board-content']}>

            {/* {pageIds.length > 0 && (
              <div style={{
                marginBottom: '20px',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '5px',
                textAlign: 'center'
              }}>
                <span style={{ fontWeight: 'bold', color: '#007bff' }}>
                  Page {currentPage} of {pageIds.length + 1}
                  {currentPage === pageIds.length + 1 && ' (Attachments)'}
                </span>
              </div>
            )} */}

            <div
              className="form-page-host"
              data-current-page={currentPage}
            >
              {isLoading && (
                <div className="application-spinner">
                  <div className="spinner "></div>
                </div>
              )}
              <FormLoader applicationName={applyingFormData.name} setFormIds={setFormIds} setPageIds={setPageIds} setIsLoaded={setIsLoaded} />
            </div>
          </div>
          {showAttachments && (
            <AddAttachment
              ref={attachmentsRef}
              signatureIds={signatureIds}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              applicationState="draft"
              applicationName={applyingFormData.name}
              jsonData={inputFields}
              embedded={true}
            />
          )}

          <FormFooter
            applicationName={applyingFormData.name}
            nextPage={nextPage}
            previousPage={previousPage}
            currentPage={currentPage}
            totalPages={pageIds?.length + 1}
            handleSubmitWithLoading={handleSubmitWithLoading}
            saveToDrafts={saveToDrafts}
            handlePageChange={handlePageChange}
            saveChanges={handleSaveChanges}
          />
        </div>
      </div>
    </div>
  )
}
export default AppForm